/**
 * Character-cluster panels (PXL): Inventory, Equipment, Character sheet,
 * Progression. All bind to the real private pushes (inventorySnapshot incl. the
 * new rolledStats + resource stacks, characterStats, levelState, masteryState,
 * reputationState, loadoutState) and send real intents. Placeholder art.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';

import type { ItemWire } from '@great-cloud/shared/client';

import type { Payload } from '../../state/store.js';
import { useGame } from '../net-context.js';
import { nameOf } from '../playerText.js';
import { Bar, Empty, IconGlyph, Money, SectionTitle, Stat, Tabs } from '../widgets.js';

import { CAPACITY_LABELS, packBreakdown, packBreakdownText } from './capacity.js';
import { cooldownLeftS, useLabel } from './consumable.js';
import { EQUIP_SLOTS, wornByCharacter } from './equipSlots.js';

function statSummary(rolled: Record<string, number>): string {
  const e = Object.entries(rolled).filter(([, v]) => v);
  return e.length ? e.map(([k, v]) => `+${v} ${k}`).join(', ') : 'no combat stats';
}

function ItemRow({ item, children }: { item: ItemWire; children?: ReactNode }): JSX.Element {
  return (
    <div className="pxl-row">
      <IconGlyph code={item.contentId} label={item.name} assetRef="TGC-UI-012" size={28} />
      <div className="grow">
        <div className={`r-${item.rarity}`}>{item.name}</div>
        <div className="pxl-faint" title={statSummary(item.rolledStats)}>
          {item.quality} · {item.rarity}
          {item.slot ? ` · ${item.slot}` : ''} · {statSummary(item.rolledStats)}
          {item.durabilityMax ? ` · dur ${item.durabilityCurrent}/${item.durabilityMax}` : ''}
        </div>
      </div>
      {children}
    </div>
  );
}

export function InventoryPanel(): JSX.Element {
  const { state, conn, store } = useGame();
  const inv = state.inventory;
  if (!inv) return <Empty>Waiting for inventory…</Empty>;
  const carried = inv.items.filter((i) => i.location === 'inventory');
  const now = Date.now();
  const using = store.isInFlight('itemUse', now);
  const resources = inv.resources;
  return (
    <div className="pxl-list">
      <SectionTitle>Items ({carried.length})</SectionTitle>
      {carried.length === 0 ? <Empty>Bag is empty.</Empty> : null}
      {carried.map((i) => (
        <ItemRow key={i.id} item={i}>
          {i.slot ? (
            <button
              className="pxl-btn tiny"
              onClick={() => conn.send('equipIntent', { itemId: i.id })}
            >
              equip
            </button>
          ) : null}
          {i.cooldownGroup ? (
            <UseButton
              itemId={i.id}
              leftS={cooldownLeftS(state.consumableReadyAt, i.cooldownGroup, now)}
              busy={using}
              onUse={() => {
                if (store.beginRequest('itemUse', Date.now()))
                  conn.send('itemUseIntent', { itemId: i.id });
              }}
            />
          ) : null}
        </ItemRow>
      ))}
      <SectionTitle>Resources ({resources.length})</SectionTitle>
      {resources.length === 0 ? <Empty>No raw materials gathered yet.</Empty> : null}
      <div className="pxl-grid2">
        {resources.map((r) => (
          <div className="pxl-row" key={`${r.contentId}:${r.quality}`}>
            <IconGlyph code={r.contentId} assetRef="TGC-UI-014" size={24} />
            <div className="grow">
              <div>{nameOf('resources', r.contentId)}</div>
              <div className="pxl-faint">
                q{r.quality} · ×{r.quantity}
              </div>
            </div>
          </div>
        ))}
      </div>
      <CapacityMeters
        capacity={inv.capacity}
        items={inv.items}
        characterId={state.ack?.characterId ?? null}
      />
    </div>
  );
}

/**
 * The bag's Use action (consumables.md §4; AD-791): one consumable per press, the
 * group's cooldown as a readout, disabled while a use is unanswered (food has no
 * cooldown, so a double-click would otherwise eat two).
 */
function UseButton({
  itemId,
  leftS,
  busy,
  onUse,
}: {
  itemId: string;
  leftS: number;
  busy: boolean;
  onUse: () => void;
}): JSX.Element {
  return (
    <button
      className="pxl-btn tiny"
      data-use-item={itemId}
      data-cooldown-s={leftS}
      disabled={busy || leftS > 0}
      onClick={onUse}
    >
      {useLabel(leftS)}
    </button>
  );
}

/** The five SP-5 cap bars (AD-775 §8); a cap in the server's warn list draws in the warning colour. */
function CapacityMeters({
  capacity,
  items,
  characterId,
}: {
  capacity: Payload<'inventorySnapshot'>['capacity'];
  items: readonly ItemWire[];
  characterId: number | null;
}): JSX.Element | null {
  if (!capacity) return null;
  // The Pack counts the whole account (§4); say where its rows are (H-1437).
  const packNote = packBreakdownText(packBreakdown(items, characterId, capacity.pack.used));
  return (
    <>
      <SectionTitle>Capacity</SectionTitle>
      {CAPACITY_LABELS.map(({ key, label, unit }) => {
        const { used, cap } = capacity[key];
        const warn = capacity.warn.includes(key);
        return (
          <div key={key} data-capacity={key} data-warn={warn ? '1' : '0'}>
            <Bar
              value={used}
              max={cap}
              color={warn ? 'var(--pxl-bad)' : undefined}
              label={`${label} ${used.toLocaleString()} / ${cap.toLocaleString()} ${unit}`}
            />
            {key === 'pack' && packNote ? (
              <div className="pxl-faint" data-pack-breakdown>
                {packNote}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export function EquipmentPanel(): JSX.Element {
  const { state, conn } = useGame();
  const inv = state.inventory;
  const stats = state.stats;
  if (!inv) return <Empty>Waiting for equipment…</Empty>;
  const equipped = wornByCharacter(inv.items, state.ack?.characterId ?? null);
  const bySlot = new Map(equipped.map((i) => [i.slot ?? '?', i]));
  return (
    <div className="pxl-list">
      <SectionTitle>Paperdoll</SectionTitle>
      {EQUIP_SLOTS.map((slot) => {
        const item = bySlot.get(slot);
        return (
          <div className="pxl-row" key={slot}>
            <IconGlyph code={slot} label={slot} assetRef="TGC-UI-027" size={26} />
            <div className="grow">
              <div className="pxl-muted" style={{ textTransform: 'capitalize' }}>
                {slot}
              </div>
              {item ? (
                <div className={`r-${item.rarity}`}>
                  {item.name} · {statSummary(item.rolledStats)}
                </div>
              ) : (
                <div className="pxl-faint">empty</div>
              )}
            </div>
            {item ? (
              <button
                className="pxl-btn tiny"
                onClick={() => conn.send('unequipIntent', { itemId: item.id })}
              >
                remove
              </button>
            ) : null}
          </div>
        );
      })}
      <SectionTitle>Derived combat stats (UIP-3)</SectionTitle>
      {stats ? (
        <>
          <Stat k="Health" v={stats.health} />
          <Stat k="Power" v={stats.power} />
          <Stat k="Armor" v={stats.armor} />
          <Stat k="Ward" v={stats.ward} />
          <div className="pxl-faint">
            Within the frozen AD-021 power envelope (skill &gt; gear).
          </div>
        </>
      ) : (
        <Empty>No derived stats yet.</Empty>
      )}
    </div>
  );
}

export function CharacterPanel(): JSX.Element {
  const { state } = useGame();
  const lvl = state.level;
  const stats = state.stats;
  const name = state.ack?.you.name ?? 'Adventurer';
  return (
    <div className="pxl-list">
      <div className="pxl-row">
        <IconGlyph code={name} label={name} assetRef="TGC-CHR-026" size={42} />
        <div className="grow">
          <div style={{ fontSize: 15 }}>{name}</div>
          <div className="pxl-faint">Level {lvl?.level ?? '—'} · classless humanoid</div>
        </div>
        <Money amount={state.wallet?.shardBalance} kind="shard" />
      </div>
      {lvl ? (
        <div>
          <Bar
            value={lvl.xp - lvl.xpIntoLevel}
            max={(lvl.xpForNextLevel ?? lvl.xp) - lvl.xpIntoLevel}
            color="var(--pxl-xp)"
            label={`XP ${lvl.xp}${lvl.xpForNextLevel ? ` / ${lvl.xpForNextLevel}` : ' (max)'}`}
          />
        </div>
      ) : null}
      <SectionTitle>Combat stats</SectionTitle>
      {stats ? (
        <div className="pxl-grid2">
          <Stat k="HP" v={stats.health} />
          <Stat k="Power" v={stats.power} />
          <Stat k="Armor" v={stats.armor} />
          <Stat k="Ward" v={stats.ward} />
        </div>
      ) : (
        <Empty>—</Empty>
      )}
      <SectionTitle>Standing</SectionTitle>
      <div className="pxl-faint">
        {state.reputation.length} faction tracks · {state.masteries.length} mastery tracks (see
        Progression, N)
      </div>
      <SectionTitle>Appearance (D-6)</SectionTitle>
      {state.ack?.you.appearance ? (
        <div className="pxl-faint">
          Build:{' '}
          <span style={{ textTransform: 'capitalize' }}>{state.ack.you.appearance.bodyType}</span> ·
          face {state.ack.you.appearance.face} · hair {state.ack.you.appearance.hair} · skin{' '}
          {state.ack.you.appearance.skin}
        </div>
      ) : (
        <div className="pxl-faint">Default appearance.</div>
      )}
    </div>
  );
}

export function ProgressionPanel(): JSX.Element {
  const { state, conn } = useGame();
  const [tab, setTab] = useState('mastery');
  const [chosen, setChosen] = useState('');
  return (
    <div>
      <Tabs
        tabs={[
          { id: 'mastery', label: `Mastery (${state.masteries.length})` },
          { id: 'rep', label: `Reputation (${state.reputation.length})` },
          { id: 'abilities', label: 'Abilities' },
        ]}
        active={tab}
        onSelect={setTab}
      />
      {tab === 'mastery' ? (
        state.masteries.length === 0 ? (
          <Empty>Gather and craft to build mastery.</Empty>
        ) : (
          <div className="pxl-list">
            {state.masteries.map((m) => (
              <div key={m.trackId}>
                <div className="pxl-stat">
                  <span className="k">
                    {m.trackId} <span className="pxl-badge">{m.kind}</span>
                  </span>
                  <span className="v">rank {m.rank}</span>
                </div>
                <Bar
                  value={m.xp - m.xpIntoRank}
                  max={(m.xpForNextRank ?? m.xp) - m.xpIntoRank}
                  height={9}
                  color="var(--pxl-accent-2)"
                />
                <div className="pxl-faint">
                  tier access {m.tierAccess} · reach {m.qualityCeiling}
                  {m.specialisationAvailable
                    ? ` · spec: ${m.specialisation ?? 'choose (rank 25)'}`
                    : ''}
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}
      {tab === 'rep' ? (
        state.reputation.length === 0 ? (
          <Empty>Complete quests to earn reputation.</Empty>
        ) : (
          <div className="pxl-list">
            {state.reputation.map((r) => (
              <div key={r.factionId}>
                <div className="pxl-stat">
                  <span className="k">
                    <IconGlyph code={r.factionId} assetRef="TGC-UI-009" size={18} />{' '}
                    {nameOf('factions', r.factionId)}
                  </span>
                  <span className="v">{r.rankName}</span>
                </div>
                <Bar
                  value={r.rep - r.repIntoRank}
                  max={(r.repForNextRank ?? r.rep) - r.repIntoRank}
                  height={9}
                  color="var(--pxl-good)"
                />
                <div className="pxl-faint">
                  unlocks: {r.recipeUnlocks.length} recipes · {r.accessGrants.length} grants ·
                  ACCESS only, never power
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}
      {tab === 'abilities' ? (
        <div className="pxl-list">
          <SectionTitle>Loadout</SectionTitle>
          {state.loadout ? (
            <>
              <Stat k="Weapon" v={state.loadout.weaponType ?? 'unarmed'} />
              <Stat k="Weapon abilities" v={state.loadout.weaponAbilities.join(', ') || '—'} />
              <Stat
                k={`Chosen (${state.loadout.chosenAbilities.length}/${state.loadout.chosenSlotsUnlocked})`}
                v={state.loadout.chosenAbilities.join(', ') || '—'}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input
                  className="pxl-input grow"
                  placeholder="comma ability ids → set loadout"
                  value={chosen}
                  onChange={(e) => setChosen(e.target.value)}
                />
                <button
                  className="pxl-btn"
                  onClick={() =>
                    conn.send('loadoutChange', {
                      chosenAbilities: chosen
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                >
                  set
                </button>
              </div>
              <div className="pxl-faint">
                Ability defs resolve from the content pack (placeholder). 23 abilities exist in
                content (P7.2).
              </div>
            </>
          ) : (
            <Empty>No loadout yet.</Empty>
          )}
        </div>
      ) : null}
    </div>
  );
}
